import { Link } from 'react-router-dom'
import { useMyHunts } from '../hooks/useHunt'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { BottomNav } from '../components/common/BottomNav'
import type { HuntHistoryItem, ProgressStatus } from '../types'

const STATUS_STYLES: Record<ProgressStatus, { label: string; className: string }> = {
  active: { label: 'In progress', className: 'bg-primary-100 text-primary-700' },
  paused: { label: 'Paused', className: 'bg-cream-200 text-brown-700' },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-700' },
  abandoned: { label: 'Abandoned', className: 'bg-cream-200 text-brown-500' },
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const remMin = minutes % 60
  return remMin > 0 ? `${hours}h ${remMin}m` : `${hours}h`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function HistoryRow({ item }: { item: HuntHistoryItem }) {
  const statusStyle = STATUS_STYLES[item.status]
  const progressPct =
    item.total_waypoints > 0
      ? Math.round((item.completed_waypoints / item.total_waypoints) * 100)
      : 0
  const canResume = item.status === 'active' || item.status === 'paused'
  const target = canResume ? `/hunts/${item.hunt_id}/play` : `/hunts/${item.hunt_id}`

  return (
    <Link to={target} className="block">
      <Card padding="md" className="hover:shadow-md transition-shadow">
        <div className="flex gap-3">
          <div className="w-16 h-16 rounded-lg bg-primary-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
            {item.hunt_cover_image_url ? (
              <img
                src={item.hunt_cover_image_url}
                alt={item.hunt_title}
                className="w-full h-full object-cover"
              />
            ) : (
              <svg className="w-7 h-7 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-brown-900 truncate">{item.hunt_title}</h3>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${statusStyle.className}`}>
                {statusStyle.label}
              </span>
            </div>
            <p className="text-xs text-brown-500 mt-0.5 capitalize">{item.hunt_difficulty}</p>
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-brown-600 mb-1">
                <span>
                  {item.completed_waypoints}/{item.total_waypoints} waypoints
                </span>
                <span>{formatDuration(item.elapsed_time_seconds)}</span>
              </div>
              <div className="w-full h-1.5 bg-cream-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
            <p className="text-xs text-brown-400 mt-2">
              {item.completed_at
                ? `Completed ${formatDate(item.completed_at)}`
                : `Started ${formatDate(item.started_at)}`}
            </p>
          </div>
        </div>
      </Card>
    </Link>
  )
}

export default function HuntHistory() {
  const { data, isLoading, error } = useMyHunts()

  return (
    <div className="min-h-screen bg-cream-50 pb-24">
      <header className="bg-gradient-to-br from-primary-500 to-primary-700 text-white">
        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link
              to="/profile"
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <h1 className="text-xl font-bold">Hunt History</h1>
              <p className="text-primary-100 text-sm">Every hunt you've played</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-3">
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <Card padding="lg" className="text-center">
            <p className="text-brown-600">Failed to load history.</p>
          </Card>
        ) : !data || data.items.length === 0 ? (
          <Card variant="parchment" padding="lg" className="text-center">
            <p className="text-brown-700 font-medium mb-1">No hunts yet</p>
            <p className="text-brown-500 text-sm mb-4">
              Start a hunt to begin your history.
            </p>
            <Link
              to="/hunts"
              className="inline-block bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Browse Hunts
            </Link>
          </Card>
        ) : (
          data.items.map((item) => <HistoryRow key={item.progress_id} item={item} />)
        )}
      </main>

      <BottomNav />
    </div>
  )
}
